

























































































































import type { PropType } from '@nuxtjs/composition-api';
// eslint-disable-next-line no-duplicate-imports
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  useContext
} from '@nuxtjs/composition-api';
import { SfLink, SfProductCard } from '@storefront-ui/vue';
import VaimoProductVariant from 'atoms/VaimoProductVariant.vue';

import { useUiNotification, useUiState } from '~/composables';
import VaimoBadge from '~/diptyqueTheme/components/atoms/VaimoBadge.vue';
import {
  getProductPreorderButtonLabel,
  getProductReplenishable,
  isProductPreoderEnabled
} from '~/diptyqueTheme/helpers/productGetters';
import type { DescriptionOption } from '~/diptyqueTheme/types/contentful';
import { DescriptionOptionEnum } from '~/diptyqueTheme/types/contentful';
import { useAddToCart } from '~/helpers/cart/addToCart';
import { ProductTypeEnum } from '~/modules/catalog/product/enums/ProductTypeEnum';
import type { Product } from '~/modules/catalog/product/types';
import { useCartStore } from '~/modules/core/stores/cart';
import { ProductStockStatus } from '~/modules/GraphQL/types';

export default defineComponent({
  name: 'VaimoProductCard',
  components: {
    VaimoProductVariant,
    SfProductCard,
    SfLink,
    VaimoBanner: () => import('atoms/VaimoBanner.vue'),
    VaimoButton: () => import('atoms/VaimoButton.vue'),
    ButtonSoldOut: () => import('atoms/ButtonSoldOut.vue'),
    ButtonNotifyMe: () => import('atoms/ButtonNotifyMe.vue'),
    EngravingPleat: () => import('molecules/EngravingPleat/EngravingPleat.vue'),
    VaimoBadge
  },

  props: {
    image: {
      type: [Array, Object, String],
      default: ''
    },
    imageSource: {
      type: String,
      default: 'magento'
    },
    hoverImage: {
      type: [Array, Object, String],
      default: null
    },
    productType: {
      type: String,
      default: 'SimpleProduct'
    },
    imageWidth: {
      type: [Number, String],
      default: null
    },
    imageHeight: {
      type: [Number, String],
      default: null
    },
    badgeLabel: {
      type: String,
      default: ''
    },
    badgeColor: {
      type: String,
      default: ''
    },
    colors: {
      type: Array,
      default: () => []
    },
    specialPrice: {
      type: [Number, String],
      default: null
    },
    showAddToCartButton: {
      type: Boolean,
      default: false
    },
    isAddedToCart: {
      type: Boolean,
      default: false
    },
    addToCartDisabled: {
      type: Boolean,
      default: false
    },
    imageTag: {
      type: String,
      default: ''
    },
    relatedProductsQnty: {
      type: [Number, String],
      default: null
    },
    capacity: {
      type: [Number, String, Array],
      default: null
    },
    variantName: {
      type: [Number, String, Array],
      default: null
    },
    title: {
      type: String,
      default: ''
    },
    subTitle: {
      type: String,
      default: ''
    },
    link: {
      type: [String, Object],
      default: null
    },
    basePrice: {
      type: [Number, String],
      default: null
    },
    regularPrice: {
      type: [Number, String],
      default: null
    },
    sizeDesktop: {
      type: String,
      required: false,
      default: 'l'
    },
    sizeMobile: {
      type: String,
      required: false,
      default: 'l'
    },
    editorialData: {
      type: [Array, null],
      default: null
    },
    hideDescriptionOptions: {
      type: Array as PropType<Array<DescriptionOption>>,
      default: () => []
    },
    stockStatus: {
      required: false,
      type: String,
      default: ''
    },
    isSalable: {
      required: false,
      type: Boolean,
      default: true
    },
    isEngravingEnabled: {
      required: false,
      type: Boolean,
      default: false
    },
    isDynamicPrice: Boolean,
    product: {
      type: Object as PropType<Product>,
      required: true
    }
  },
  emits: ['add-to-cart'],
  setup(props) {
    const {
      app: { i18n, router }
    } = useContext();
    const cartStore = useCartStore();
    const productCardRef = ref();
    const { openNotifyMeModal } = useUiState();
    const { addItemToCart } = useAddToCart();
    const { send: sendNotification } = useUiNotification();
    const descriptionClasses = computed(() => {
      return {
        's:hidden': props.hideDescriptionOptions.includes(
          DescriptionOptionEnum.HIDE_ON_MOBILE
        ),
        'desktop:hidden': props.hideDescriptionOptions.includes(
          DescriptionOptionEnum.HIDE_ON_DESKTOP
        )
      };
    });
    const desktopSizesMap = {
      l: 'desktop-l',
      m: 'desktop-m',
      s: 'desktop-s'
    };
    const mobileSizesMap = {
      l: 'mobile-l',
      m: 'mobile-m',
      s: 'mobile-s'
    };
    // TODO: Make new logic with hover effect
    const getSizeClasses = computed(() => {
      return [
        desktopSizesMap[props.sizeDesktop],
        mobileSizesMap[props.sizeMobile]
      ];
    });

    const getProductPrice = computed(() => {
      const price = (props as { regularPrice: number }).regularPrice;
      return (props as { isDynamicPrice: boolean }).isDynamicPrice
        ? i18n.t('From {price}', {
            price
          })
        : price;
    });

    const getProductBasePrice = computed(() => {
      const price = props.basePrice;
      return props.isDynamicPrice
        ? i18n.t('From {price}', {
            price
          })
        : price;
    });

    const isMerging = computed(() => cartStore.is_data_loading);
    const isEditorialDataAvailable = computed(() => {
      return props.editorialData !== null && props.editorialData.length;
    });

    const isOutOfStock = computed<boolean>(() => {
      return (
        props.stockStatus === ProductStockStatus.OutOfStock || !props.isSalable
      );
    });

    const isSoldOut = computed<boolean>(() => {
      return (
        (props.stockStatus === ProductStockStatus.OutOfStock ||
          !props.isSalable) &&
        !getProductReplenishable(props.product)
      );
    });

    const canNotify = computed<boolean>(() => {
      return (
        (props.stockStatus === ProductStockStatus.OutOfStock ||
          !props.isSalable) &&
        getProductReplenishable(props.product)
      );
    });

    const productData = computed<{
      id: number | null;
      isReplenishable: boolean;
      isPreOrder: boolean;
      preOrderAddToCartLabel: string;
    }>(() => ({
      id: props.product?.id || null,
      isReplenishable: getProductReplenishable(props.product),
      isPreOrder: isProductPreoderEnabled(props.product),
      preOrderAddToCartLabel: getProductPreorderButtonLabel(props.product)
    }));

    const goToProduct = (link) => {
      router.push(link);
    };

    const isAddingToCart = ref(false);
    const addToCart = async () => {
      isAddingToCart.value = true;

      try {
        await addItemToCart({ product: props.product, quantity: 1 });
      } catch (error) {
        sendNotification({
          id: Symbol('product_add_error'),
          message: String(i18n.t('This product is currently unavailable')),
          persist: false,
          type: 'danger',
          area: 'top'
        });
        await router.push({ name: 'error' });
      } finally {
        isAddingToCart.value = false;
      }
    };

    // Toggle Notify Me modal
    const notifyMe = () => {
      if (!productData.value.id) return;
      openNotifyMeModal(productData.value.id);
    };

    onMounted(() => {
      // Making svg icons not accessible to pass the Level Access test
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      const icons = productCardRef.value?.$el.querySelectorAll('span.sf-icon');
      if (icons && icons.length) {
        icons.forEach((el) => {
          el.setAttribute('aria-hidden', 'true');
        });
      }
    });

    const isAddToCartButtonShown = computed(
      () =>
        !isOutOfStock.value &&
        !isMerging.value &&
        props.productType === ProductTypeEnum.SIMPLE_PRODUCT
    );
    const isCreateThisSetButtonShown = computed(
      () =>
        !isOutOfStock.value &&
        !isMerging.value &&
        props.productType === ProductTypeEnum.BUNDLE_PRODUCT
    );

    const hasActionButton = computed(
      () =>
        isSoldOut.value ||
        canNotify.value ||
        isAddToCartButtonShown.value ||
        isCreateThisSetButtonShown.value
    );

    const goToEngravingInfo = (link) => {
      router.push(`${link}?engraving=1`);
    };

    return {
      goToProduct,
      getProductPrice,
      getProductBasePrice,
      isOutOfStock,
      isSoldOut,
      canNotify,
      hasActionButton,
      getSizeClasses,
      isEditorialDataAvailable,
      cartStore,
      isMerging,
      isAddToCartButtonShown,
      addToCart,
      notifyMe,
      openNotifyMeModal,
      isAddingToCart,
      isCreateThisSetButtonShown,
      productData,
      productCardRef,
      goToEngravingInfo,
      descriptionClasses
    };
  }
});
